// Ren, isomorf SHA-256 + HMAC-SHA256 (ingen Node-crypto/Web-Crypto-avhengighet),
// slik at kontraktene kan signere/verifisere meldinger både i nettleser og på
// server deterministisk. Verifisert mot kjente testvektorer (FIPS 180-4 / RFC 4231).
// Brukes bl.a. til integritets-signering av CoT-meldinger (se cotsign.ts).
const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];
const rotr = (x, n) => (x >>> n) | (x << (32 - n));
/** SHA-256 av byte-array → 32-byte digest (Uint8Array). */
export function sha256Bytes(msg) {
    const h = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
    const ml = msg.length * 8;
    // Padding: 0x80, deretter nuller til lengde ≡ 56 (mod 64), så 64-bit lengde.
    const withPad = new Uint8Array((((msg.length + 8) >> 6) + 1) << 6);
    withPad.set(msg);
    withPad[msg.length] = 0x80;
    const dv = new DataView(withPad.buffer);
    dv.setUint32(withPad.length - 4, ml >>> 0, false);
    dv.setUint32(withPad.length - 8, Math.floor(ml / 0x100000000), false);
    const w = new Uint32Array(64);
    for (let off = 0; off < withPad.length; off += 64) {
        for (let i = 0; i < 16; i++)
            w[i] = dv.getUint32(off + i * 4, false);
        for (let i = 16; i < 64; i++) {
            const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
            const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
            w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
        }
        let [a, b, c, d, e, f, g, hh] = h;
        for (let i = 0; i < 64; i++) {
            const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
            const ch = (e & f) ^ (~e & g);
            const t1 = (hh + S1 + ch + K[i] + w[i]) >>> 0;
            const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
            const maj = (a & b) ^ (a & c) ^ (b & c);
            const t2 = (S0 + maj) >>> 0;
            hh = g;
            g = f;
            f = e;
            e = (d + t1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (t1 + t2) >>> 0;
        }
        h[0] = (h[0] + a) >>> 0;
        h[1] = (h[1] + b) >>> 0;
        h[2] = (h[2] + c) >>> 0;
        h[3] = (h[3] + d) >>> 0;
        h[4] = (h[4] + e) >>> 0;
        h[5] = (h[5] + f) >>> 0;
        h[6] = (h[6] + g) >>> 0;
        h[7] = (h[7] + hh) >>> 0;
    }
    const out = new Uint8Array(32);
    const odv = new DataView(out.buffer);
    for (let i = 0; i < 8; i++)
        odv.setUint32(i * 4, h[i], false);
    return out;
}
const enc = new TextEncoder();
function toHex(b) {
    let s = '';
    for (const x of b)
        s += x.toString(16).padStart(2, '0');
    return s;
}
/** SHA-256 av en UTF-8-streng → hex. */
export function sha256Hex(msg) {
    return toHex(sha256Bytes(enc.encode(msg)));
}
/** HMAC-SHA256 (nøkkel + melding som byte) → 32-byte digest. */
export function hmacSha256Bytes(key, msg) {
    const block = 64;
    let k = key.length > block ? sha256Bytes(key) : key;
    const kPad = new Uint8Array(block);
    kPad.set(k);
    const oKey = new Uint8Array(block), iKey = new Uint8Array(block);
    for (let i = 0; i < block; i++) {
        oKey[i] = kPad[i] ^ 0x5c;
        iKey[i] = kPad[i] ^ 0x36;
    }
    const inner = sha256Bytes(concat(iKey, msg));
    return sha256Bytes(concat(oKey, inner));
}
/** HMAC-SHA256 (nøkkel + melding som UTF-8-strenger) → hex. */
export function hmacSha256Hex(key, msg) {
    return toHex(hmacSha256Bytes(enc.encode(key), enc.encode(msg)));
}
function concat(a, b) {
    const out = new Uint8Array(a.length + b.length);
    out.set(a);
    out.set(b, a.length);
    return out;
}
/** Konstant-tids sammenligning av to hex-strenger (mot timing-angrep). */
export function timingSafeEqualHex(a, b) {
    if (a.length !== b.length)
        return false;
    let diff = 0;
    for (let i = 0; i < a.length; i++)
        diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
    return diff === 0;
}
//# sourceMappingURL=hash.js.map